<template>
	<div>
		<BaseHeader :title="$t('work_steps').toString()" />
		<div class="mx-4">
			<div class="flex p-4">
				<p class="text-left">
					{{ $t('define_work_step_description') }}
				</p>
			</div>
			<div class="flex p-4">
				<BaseInput v-model="newTask.name" :label="$t('new_work_step')" class="flex-grow mr-4" @keyup.enter="add" />
				<div class="mt-8">
					<BaseButton @click="add" :disabled="!newTask.name.length">{{ $t('add') }}</BaseButton>
				</div>
			</div>
			<div class="grid lg:gap-32 text-left mx-4" :class="{ 'md:grid-cols-2': !isUserAllowedInLevel1 }">
				<div>
					<div class="font-bold text-lg">{{ $t('global_working_steps') }}</div>
					<div class="mt-4 grid xxl:grid-cols-2 gap-x-12">
						<RepairPlanTaskRow
							v-for="(task, index) in sortedGlobalTasks"
							:key="task.id"
							:task="task"
							:index="index"
							@delete="deleteRow(task)"
							@change="registerChange(false)"
							:disabled="!isUserAllowedInLevel1"
						/>
					</div>
				</div>

				<div v-if="!isUserAllowedInLevel1">
					<div class="font-bold text-lg">{{ $t('company_wide_working_steps') }}</div>
					<div class="mt-4 grid xxl:grid-cols-2 gap-x-12">
						<RepairPlanTaskRow
							v-for="(task, index) in sortedTasks"
							:key="task.id"
							:task="task"
							:index="index"
							@change="registerChange(true)"
							@delete="deleteRow(task)"
							class="mb-2"
						/>
					</div>
				</div>
			</div>
			<div class="text-right" v-if="tasks && tasks.length">
				<BaseButton @click="save" class="m-4 btn-blue">{{ $t('general.save') }}</BaseButton>
			</div>
		</div>
	</div>
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader';
import BaseInput from '@/components/forms/inputs/BaseInput';
import BaseButton from '@/components/forms/buttons/BaseButton';
import apiDefaults from '@/api/defaultConfiguration';
import { RepairPlanTaskApiFactory } from '@/api/generated';
import defaultConfiguration from '@/api/defaultConfiguration';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import RepairPlanTaskRow from '@/components/rows/RepairPlanTaskRow';
import { emptyCatch, makeShadowCopy } from '@/plugins/Utils';
import Authentication from '@/mixins/Authentication';

const repairPlanTaskApiFactory = RepairPlanTaskApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios);

export default {
	name: 'RepairPlanTask',
	components: { RepairPlanTaskRow, BaseButton, BaseInput, BaseHeader },
	mixins: [LoadingSpinner, Authentication],
	data() {
		return {
			newTask: { name: '', id: 0 },
			newTasks: [],
			tasks: [],
			deleteList: [],
			sortedTasks: [],
			sortedGlobalTasks: [],
			timer: null,
			doRefresh: false,
		};
	},
	mounted() {
		this.fetchSteps();
	},
	computed: {
		globalTasks() {
			return this.tasks?.filter(task => task.is_global);
		},
		companyTasks() {
			return this.tasks?.filter(task => !task.is_global);
		},
	},
	methods: {
		registerChange(isCompany) {
			clearTimeout(this.timer);
			this.timer = setTimeout(() => {
				if (isCompany) {
					this.sortedTasks = [...this.companyTasks, ...this.newTasks].sort((a, b) => a.name.localeCompare(b.name));
				} else {
					this.sortedGlobalTasks = [...this.globalTasks, ...this.newTasks].sort((a, b) => a.name.localeCompare(b.name));
				}
			}, 3000);
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
		},
		fetchSteps() {
			repairPlanTaskApiFactory
				.repairPlanTasksGet()
				.then(response => {
					this.tasks = response.data.data.map(makeShadowCopy);
					if (this.isUserAllowedInLevel1) {
						this.sortedGlobalTasks = [...this.globalTasks, ...this.newTasks].sort((a, b) => a.name.localeCompare(b.name));
					} else {
						this.sortedGlobalTasks = this.globalTasks;
						this.sortedTasks = [...this.companyTasks, ...this.newTasks].sort((a, b) => a.name.localeCompare(b.name));
					}
				})
				.catch(emptyCatch);
		},
		async save() {
			this.setLoadingStatus(true);
			const promise = [];
			this.newTasks.forEach(task => {
				promise.push(repairPlanTaskApiFactory.repairPlanTasksPost({ name: task.name }));
			});
			this.deleteList.forEach(task => {
				RepairPlanTaskApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					.repairPlanTasksIdDelete(task.id)
					.then(response => {});
			});

			for (let i = 0; i < this.tasks.length; i++) {
				const row = this.tasks[i];
				const isChanged = row._shadow.name !== row.name;
				if (isChanged && (!row.is_global || this.isUserAllowedInLevel1)) {
					promise.push(repairPlanTaskApiFactory.repairPlanTasksIdPut(row.id, row));
				}
			}

			Promise.all(promise)
				.then(response => {
					this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
					this.$store.commit('setConfirmLeaving', false);
					this.setLoadingStatus(false);
					this.fetchSteps();
					this.deleteList = [];
					this.newTasks = [];
					this.newTask = { name: '', id: 0 };
				})
				.catch(error => {
					this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
					this.setLoadingStatus(false);
				});
		},
		deleteRow(task) {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			if (task.isNew) {
				const index = this.newTasks.findIndex(e => e.id === task.id);
				this.newTasks.splice(index, 1);
			} else {
				this.deleteList.push(task);
				const index = this.tasks.findIndex(e => e.id === task.id);
				this.tasks.splice(index, 1);
			}
			if (this.isUserAllowedInLevel1) {
				this.sortedGlobalTasks = [...this.globalTasks, ...this.newTasks].sort((a, b) => a.name.localeCompare(b.name));
			} else {
				this.sortedGlobalTasks = this.globalTasks;
				this.sortedTasks = [...this.companyTasks, ...this.newTasks].sort((a, b) => a.name.localeCompare(b.name));
			}
		},
		findIndex(array) {
			let largest = 0;

			for (let i = 0; i < array.length; i++) {
				if (array[i].id > largest) {
					largest = array[i].id;
				}
			}
			return largest + 1;
		},
		add() {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			if (this.newTasks.length === 0) {
				if (this.isUserAllowedInLevel1) {
					this.newTask.id = this.findIndex(this.globalTasks);
				} else {
					this.newTask.id = this.findIndex(this.companyTasks);
				}
			} else {
				this.newTask.id = this.newTask.id + 1;
			}
			this.newTasks.push({ name: this.newTask.name, isNew: true, id: this.newTask.id });
			this.newTask.name = '';
			if (this.isUserAllowedInLevel1) {
				this.sortedGlobalTasks = [...this.globalTasks, ...this.newTasks].sort((a, b) => a.name.localeCompare(b.name));
			} else {
				this.sortedGlobalTasks = this.globalTasks;
				this.sortedTasks = [...this.companyTasks, ...this.newTasks].sort((a, b) => a.name.localeCompare(b.name));
			}
		},
	},
};
</script>

<style scoped></style>
